import React, { useState } from 'react';
import { Item, Board, useCanvasStore } from '@/stores/canvasStore';
import { TweetPreviewCard, isTweetItem } from '@/components/items/TweetPreviewCard';
import { SocialMediaCard } from '@/components/items/SocialMediaCard';
import { ScrapedCreativeCard } from '@/components/items/ScrapedCreativeCard';
import { isImageFirstSocial, type SocialCardContent } from '@/lib/socialCard';
import { decodeHtmlEntities } from '@/lib/htmlEntities';
import {
  FileText,
  File,
  StickyNote,
  Type,
  Image as ImageIcon,
  Link2,
  Film,
  Play,
  Clock,
  Pin,
  Share2,
  Trash2,
  Eye,
  ExternalLink,
  Maximize2,
  SquarePlus,
  LocateFixed,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { copyShareLink } from '@/lib/sharing';
import { getContrastColor, getStickyColorValue } from '@/lib/colorUtils';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { canAddToCanvas } from '@/lib/discoverToCanvas';
import { deleteDiscoverItem } from '@/lib/discoverDelete';
import { AddToCanvasDialog } from './AddToCanvasDialog';
import { PdfReaderDialog } from '@/components/items/PdfReader';
import { PdfCover } from '@/components/items/PdfThumbnail';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface WallItemCardProps {
  item: Item;
  board?: Board;
  boards?: Board[];
  onClick: () => void;
  onPin?: () => void;
  onDelete?: () => void;
  isPinned?: boolean;
  compact?: boolean;
}

const typeLabels: Record<string, string> = {
  image: 'Image',
  sticky: 'Note',
  text_card: 'Text',
  document: 'Doc',
  file: 'File',
  link: 'Link',
  video: 'Video',
  scraped_creative: 'Top Creative',
};

// Helper to extract first URL from text
const extractFirstUrl = (text: string): string | null => {
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
  const match = text.match(urlRegex);
  return match ? match[0] : null;
};

const TypeIcon = ({ type, className }: { type: string; className?: string }) => {
  const iconProps = { className: cn('w-3 h-3', className) };
  switch (type) {
    case 'image': return <ImageIcon {...iconProps} />;
    case 'sticky': return <StickyNote {...iconProps} />;
    case 'text_card': return <Type {...iconProps} />;
    case 'document': return <FileText {...iconProps} />;
    case 'file': return <File {...iconProps} />;
    case 'link': return <Link2 {...iconProps} />;
    case 'video': return <Film {...iconProps} />;
    case 'scraped_creative': return <Sparkles {...iconProps} />;
    default: return null;
  }
};

// Helper to get board label - shows "Home Board" for root board, "Not in any board" for null
const getBoardLabel = (item: Item, board?: Board, boards?: Board[]): { label: string; color: string | null } => {
  // No board assigned — created in Wall without a home
  if (!item.board_id) {
    return { label: 'Not in any board', color: null };
  }
  
  // Find the board if not passed directly
  const foundBoard = board || boards?.find(b => b.id === item.board_id);
  
  if (!foundBoard) {
    return { label: 'Not in any board', color: null };
  }
  
  // Check if it's the Home board (root board with no parent)
  const isHomeBoard = foundBoard.name === 'Home' || foundBoard.parent_id === null;
  
  return {
    label: isHomeBoard ? 'Home Board' : foundBoard.name,
    color: foundBoard.icon_color || '#6366F1'
  };
};

// Context menu component
interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  onShare: () => void;
  onDelete: () => void;
  onAddToCanvas?: () => void;
  onOpenOnCanvas?: () => void;
  onRefreshPreview?: () => void;
}

const menuItemClass = 'w-full px-4 py-2.5 text-left flex items-center gap-3 rounded-lg hover:bg-wash hover:text-ink text-ink-2 transition-colors';

const ContextMenu = ({ x, y, onClose, onShare, onDelete, onAddToCanvas, onOpenOnCanvas, onRefreshPreview }: ContextMenuProps) => {
  React.useEffect(() => {
    const handleClickOutside = () => onClose();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [onClose]);

  return (
    <div
      className="fixed z-50 bg-paper-raised rounded-xl shadow-xl border border-line py-1 min-w-[160px]"
      style={{ left: x, top: y }}
      onClick={(e) => e.stopPropagation()}
    >
      {onAddToCanvas && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAddToCanvas();
            onClose();
          }}
          className={menuItemClass}
        >
          <SquarePlus className="w-4 h-4" />
          Add to canvas…
        </button>
      )}
      {onOpenOnCanvas && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenOnCanvas();
            onClose();
          }}
          className={menuItemClass}
        >
          <LocateFixed className="w-4 h-4" />
          Open on canvas
        </button>
      )}
      {onRefreshPreview && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRefreshPreview();
            onClose();
          }}
          className={menuItemClass}
        >
          <RefreshCw className="w-4 h-4" />
          Refresh preview
        </button>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onShare();
          onClose();
        }}
        className={menuItemClass}
      >
        <Share2 className="w-4 h-4" />
        Share
      </button>
      <div className="h-px bg-line my-1" />
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
          onClose();
        }}
        className="w-full px-4 py-2.5 text-left flex items-center gap-3 rounded-lg hover:bg-danger hover:text-danger-foreground text-danger transition-colors"
      >
        <Trash2 className="w-4 h-4" />
        Delete
      </button>
    </div>
  );
};

export function WallItemCard({ item, board, boards, onClick, onPin, onDelete, isPinned = false, compact = false }: WallItemCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [showAddToCanvas, setShowAddToCanvas] = useState(false);
  const openItemOnCanvas = useCanvasStore(s => s.openItemOnCanvas);
  const isMobile = useIsMobile();
  const content = item.content as Record<string, unknown>;

  // Determine if this is a card that should use 3:2 aspect ratio
  const isPdfFile = item.item_type === 'file' && (item.content as { mimeType?: string }).mimeType === 'application/pdf';
  // A dead link image (expired CDN URL) falls back to the text card
  const [linkImageError, setLinkImageError] = useState(false);
  const useFixedAspect = ['sticky', 'text_card', 'document', 'video'].includes(item.item_type) || isPdfFile;

  // Get board label using helper
  const { label: boardLabel, color: boardColor } = getBoardLabel(item, board, boards);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleShare = async () => {
    await copyShareLink('item', item.id);
  };

  // The canvas is desktop-only (mobile is forced into Discover), so hide the jump there
  const itemBoard = board || boards?.find(b => b.id === item.board_id);
  const canOpenOnCanvas = !isMobile && !!itemBoard;

  const handleDelete = async () => {
    if (await deleteDiscoverItem(item)) onDelete?.();
  };

  // Check if item has image-dominant display (for board tag styling)
  const isImageDominant = (
    item.item_type === 'image' || 
    item.item_type === 'scraped_creative' ||
    (item.item_type === 'link' && !!(content.image))
  );

  // Board tag component - used at bottom right of all cards, only visible on hover
  const BoardTag = () => (
    <div className={cn(
      'absolute bottom-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-opacity duration-200',
      isHovered ? 'opacity-100' : 'opacity-0',
      isImageDominant
        ? 'bg-black/30 backdrop-blur text-white/70'
        : 'bg-muted text-muted-foreground'
    )}>
      {boardColor ? (
        <>
          <div
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: boardColor }}
          />
          <span className="truncate max-w-[100px]">{boardLabel}</span>
        </>
      ) : (
        <span className="truncate max-w-[120px] italic">{boardLabel}</span>
      )}
    </div>
  );

  const renderContent = () => {
    switch (item.item_type) {
      case 'image':
        const imageCaption = content.summary as string || content.alt as string || content.filename as string;
        const isGif = (content.mimeType as string) === 'image/gif' || 
          (content.url as string)?.toLowerCase().endsWith('.gif') || 
          (content.alt as string)?.toLowerCase().endsWith('.gif');
        return (
          <div className="relative h-full">
            {isGif && (
              <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide z-10">
                GIF
              </span>
            )}
            <img
              src={content.url as string}
              alt={content.alt as string || 'Image'}
              className="w-full rounded-xl object-cover"
              loading="lazy"
              style={{ minHeight: '200px' }}
            />
            {/* Caption overlay — only if there's meaningful caption text */}
            {imageCaption && imageCaption !== 'Image' && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent p-3 pt-8 rounded-b-xl">
                <p className="text-white/90 text-xs leading-relaxed line-clamp-1">
                  {imageCaption}
                </p>
              </div>
            )}
          </div>
        );

      case 'sticky':
        const stickyColor = (content.color as string) || 'yellow';
        const stickyCustomColor = content.customColor as string | undefined;
        const stickyColorCode = content.colorCode as string | undefined;
        const bgColor = getStickyColorValue(stickyColor, stickyCustomColor);
        const textColor = stickyCustomColor ? getContrastColor(stickyCustomColor) : '#17181A';
        return (
          <div
            className="p-5 h-full flex flex-col relative"
            style={{ backgroundColor: bgColor }}
          >
            <p 
              className="text-sm leading-relaxed whitespace-pre-wrap line-clamp-6 flex-1"
              style={{ color: textColor }}
            >
              {content.text as string || 'Empty note'}
            </p>
            {/* Color code label — bottom right */}
            {stickyColorCode && (
              <div 
                className="absolute bottom-2 right-2.5 text-[9px] font-mono"
                style={{ opacity: 0.3, color: textColor }}
              >
                {stickyColorCode}
              </div>
            )}
            {/* Type indicator at bottom left */}
            <div className="mt-auto pt-2 flex items-center gap-1.5" style={{ color: '#66655F' }}>
              <StickyNote className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">Note</span>
            </div>
          </div>
        );

      case 'text_card':
        const textContent = item.content as { body?: string; title?: string; partnr_generated?: boolean };
        const bodyText = (textContent.body || '').replace(/<[^>]*>/g, '');
        const detectedUrl = extractFirstUrl(bodyText);
        
        // If text card contains a URL, render as link preview
        if (detectedUrl) {
          let linkHostname = '';
          try {
            linkHostname = new URL(detectedUrl).hostname;
          } catch {
            linkHostname = detectedUrl;
          }
          
          return (
            <div className={cn(
              "bg-paper-raised overflow-hidden h-full flex flex-col",
              textContent.partnr_generated && "border-2 border-dashed border-partnr/25"
            )}>
              <div className="p-4 flex-1 flex flex-col min-h-[100px]">
                <p className="text-ink-3 text-xs mb-1">
                  {linkHostname}
                </p>
                {textContent.title && (
                  <h3 className="text-ink font-medium text-sm mb-1 line-clamp-2">
                    {textContent.title}
                  </h3>
                )}
                <p className="text-ink-3 text-xs line-clamp-3 flex-1">
                  {bodyText.replace(detectedUrl, '').trim() || detectedUrl}
                </p>
                {/* Type indicator */}
                <div className="mt-auto pt-3 flex items-center gap-1.5 text-ink-3">
                  <Link2 className="w-3 h-3" />
                  <span className="text-xs uppercase tracking-wide">Link</span>
                </div>
              </div>
            </div>
          );
        }
        
        const preview = bodyText.slice(0, 250);
        return (
          <div className={cn(
            "p-5 h-full flex flex-col bg-paper-raised",
            textContent.partnr_generated && "border-2 border-dashed border-partnr/25"
          )}>
            {textContent.title && (
              <h3 className="font-semibold text-ink mb-2 line-clamp-2">
                {textContent.title}
              </h3>
            )}
            <p className="text-ink-2 text-sm leading-relaxed line-clamp-6 flex-1">
              {preview}
              {bodyText.length > 250 && '...'}
            </p>
            {/* Type indicator */}
            <div className="mt-auto pt-2 flex items-center gap-1.5 text-ink-3">
              <FileText className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">Text</span>
            </div>
          </div>
        );

      case 'document':
        // Show more text preview for documents - use 'body' field which contains the HTML
        const docText = (content.body as string || '').replace(/<[^>]*>/g, '');
        const docPreview = docText.slice(0, 300);
        return (
          <div className="bg-paper-raised overflow-hidden h-full flex flex-col">
            <div className="px-5 pt-5 pb-3 border-b border-line">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-sm font-semibold text-ink truncate">
                  {content.title as string || 'Untitled Document'}
                </h3>
              </div>
            </div>
            <div className="p-5 flex-1">
              <p className="text-ink-3 text-sm leading-relaxed line-clamp-5">
                {docPreview}
                {docText.length > 300 && '...'}
              </p>
            </div>
            {/* Type indicator */}
            <div className="px-5 pb-4 flex items-center gap-1.5 text-ink-3">
              <File className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">Document</span>
            </div>
          </div>
        );

      case 'file': {
        const isPdf = (content.mimeType as string) === 'application/pdf';
        if (isPdf) {
          return (
            <WallPdfPreview
              url={content.url as string}
              filename={content.filename as string || 'Document.pdf'}
              size={content.size as number || 0}
            />
          );
        }
        return (
          <div className="p-4 bg-paper-sunken h-full flex flex-col">
            <div className="flex items-center gap-3 flex-1">
              <div className="p-2 rounded-lg bg-line">
                <File className="h-5 w-5 text-ink-3" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-ink truncate text-sm">
                  {content.filename as string}
                </p>
                <p className="text-xs text-ink-3">
                  {formatFileSize(content.size as number || 0)}
                </p>
              </div>
            </div>
            {/* Type indicator */}
            <div className="mt-auto pt-2 flex items-center gap-1.5 text-ink-3">
              <File className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">File</span>
            </div>
          </div>
        );
      }

      case 'scraped_creative':
        // Same card as the canvas; the tile opens the Discover preview on click
        return <ScrapedCreativeCard item={item} layout="natural" framed={false} />;

      case 'link': {
        // X/Twitter tweet bookmark — rich preview card
        if (isTweetItem(item)) {
          return <TweetPreviewCard item={item} />;
        }

        // Instagram and TikTok: the same image-first card as the canvas, at the media's own ratio
        if (isImageFirstSocial(content as SocialCardContent)) {
          return <SocialMediaCard item={item} layout="natural" openOnClick={false} />;
        }

        const linkUrl = content.url as string;

        // YouTube links get video-card-style embed preview
        const ytId = getYouTubeId(linkUrl);
        if (ytId) {
          const ytTitle = content.title as string || 'YouTube Video';
          const ytThumb = content.image as string || `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
          return (
            <WallYouTubePreview videoId={ytId} title={ytTitle} thumbnail={ytThumb} url={linkUrl} />
          );
        }

        let linkHostname = '';
        try {
          linkHostname = new URL(linkUrl).hostname.replace('www.', '');
        } catch {
          linkHostname = linkUrl;
        }
        const linkTitle = decodeHtmlEntities(content.title as string) || linkHostname;
        // Use screenshot as fallback when no OG image (matches canvas LinkCard behavior)
        const linkImage = (content.image as string) || (content.screenshot as string) || null;

        if (linkImage && !linkImageError) {
          // IMAGE-DOMINANT LAYOUT — 85% image, minimal overlay
          return (
            <div className="relative h-full flex flex-col">
              {/* Full card image */}
              {/* Short alt: a broken image must never print the caption over the card */}
              <img
                src={linkImage}
                alt=""
                className="w-full rounded-xl object-cover"
                loading="lazy"
                style={{ minHeight: '200px' }}
                onError={() => setLinkImageError(true)}
              />
              
              {/* Bottom overlay — title + domain on dark gradient */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent p-4 pt-12 rounded-b-xl">
                <h3 className="text-white font-medium text-sm leading-snug line-clamp-2 mb-1">
                  {linkTitle}
                </h3>
                <div className="flex items-center gap-1.5">
                  {content.favicon && (
                    <img
                      src={content.favicon as string}
                      alt=""
                      className="w-3 h-3 rounded-sm"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <span className="text-white/60 text-xs truncate">{linkHostname}</span>
                </div>
              </div>
            </div>
          );
        } else {
          // NO IMAGE FALLBACK — clean text card style
          return (
            <div className="bg-card p-5 h-full flex flex-col" style={{ minHeight: '120px' }}>
              <p className="text-muted-foreground text-xs mb-2">{content.siteName as string || linkHostname}</p>
              <h3 className="text-foreground font-medium text-sm mb-1 line-clamp-2">
                {linkTitle}
              </h3>
              {content.description && (
                <p className="text-muted-foreground text-xs line-clamp-2 flex-1">
                  {decodeHtmlEntities(content.description as string)}
                </p>
              )}
              <div className="mt-auto pt-3 flex items-center gap-1.5 text-muted-foreground">
                <Link2 className="w-3 h-3" />
                <span className="text-xs uppercase tracking-wide">Link</span>
              </div>
            </div>
          );
        }
      }

      case 'video':
        const duration = content.duration as number || 0;
        const mins = Math.floor(duration / 60);
        const secs = Math.floor(duration % 60);
        const durationStr = `${mins}:${secs.toString().padStart(2, '0')}`;
        const videoTitle = content.filename as string || content.summary as string || 'Video';
        
        return (
          <div className="relative bg-paper-raised overflow-hidden h-full flex flex-col">
            {/* Thumbnail area */}
            <div className="relative flex-1 min-h-0 bg-black/10 dark:bg-black/30">
              {content.thumbnailUrl ? (
                <img 
                  src={content.thumbnailUrl as string} 
                  alt={content.filename as string || 'Video thumbnail'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-line">
                  <Film className="w-8 h-8 text-ink-3" />
                </div>
              )}
              
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-10 h-10 rounded-full bg-paper-raised/90 flex items-center justify-center shadow-md">
                  <Play className="w-5 h-5 text-ink ml-0.5" fill="currentColor" />
                </div>
              </div>
              
              {/* Duration badge */}
              {duration > 0 && (
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {durationStr}
                </div>
              )}
            </div>
            
            {/* White bottom section with title, type, and board tag */}
            <div className="p-3 bg-paper-raised min-h-[80px] flex flex-col">
              <p className="font-medium text-ink truncate text-sm mb-2">
                {videoTitle}
              </p>
              {/* Type indicator under name */}
              <div className="mt-auto flex items-center gap-1.5 text-ink-3">
                <Film className="w-3 h-3" />
                <span className="text-xs uppercase tracking-wide">Video</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div
        className={cn(
          'relative rounded-xl overflow-hidden cursor-pointer',
          'bg-paper-raised',
          'transition-all duration-300',
          'hover:shadow-lg hover:-translate-y-1',
          'group',
          compact && 'scale-95'
        )}
        style={{
          // Fixed 3:2 aspect ratio for text-based and video cards
          aspectRatio: useFixedAspect ? '3/2' : undefined
        }}
        onClick={onClick}
        onContextMenu={handleContextMenu}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {renderContent()}
        
        {/* Hover Actions - Top Right */}
        <div className={cn(
          'absolute top-3 right-3 flex gap-1 transition-opacity duration-200',
          isHovered ? 'opacity-100' : 'opacity-0'
        )}>
          {onPin && (
            <button 
              className={cn(
                'p-2 backdrop-blur rounded-lg transition-colors',
                isPinned 
                  ? 'bg-ink text-paper-raised' 
                  : 'bg-black/50 dark:bg-white/20 text-white hover:bg-black/70 dark:hover:bg-white/30'
              )}
              onClick={(e) => {
                e.stopPropagation();
                onPin();
              }}
              title={isPinned ? 'Pinned' : 'Pin to Wall'}
            >
              <Pin className="w-4 h-4" />
            </button>
          )}
          <button
            className="p-2 backdrop-blur rounded-lg transition-colors bg-black/50 dark:bg-white/20 text-white hover:bg-danger hover:text-danger-foreground"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Board indicator - Bottom Right - Shows on hover */}
        {!compact && (
          <div className={cn(
            'transition-opacity duration-200',
            isHovered ? 'opacity-100' : 'opacity-0'
          )}>
            <BoardTag />
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onShare={handleShare}
          onDelete={handleDelete}
          onAddToCanvas={canAddToCanvas(item) ? () => setShowAddToCanvas(true) : undefined}
          onOpenOnCanvas={canOpenOnCanvas ? () => { openItemOnCanvas(item); } : undefined}
          onRefreshPreview={item.item_type === 'link' && !isTweetItem(item)
            ? () => { void import('@/lib/linkPreviewRefresh').then((m) => m.refreshLinkPreviewFromMenu(item)); }
            : undefined}
        />
      )}

      {showAddToCanvas && (
        <AddToCanvasDialog item={item} onClose={() => setShowAddToCanvas(false)} />
      )}
    </>
  );
}

// ── Helper: extract YouTube video ID ───────────────────────────────────

function getYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) return u.searchParams.get('v');
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('/')[0];
    if (u.pathname.includes('/embed/')) return u.pathname.split('/embed/')[1]?.split('?')[0] || null;
  } catch { /* invalid url */ }
  return null;
}

// ── Wall PDF Preview sub-component ─────────────────────────────────────

function WallPdfPreview({ url, filename, size }: { url: string; filename: string; size: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const displaySize = size ? formatFileSize(size) : '';

  return (
    <>
      <div
        className="relative h-full flex flex-col bg-paper-sunken cursor-pointer group"
        onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
        style={{ minHeight: '200px' }}
      >
        {/* First page */}
        <div className="absolute inset-x-5 top-5 bottom-16 bg-white rounded-[5px] ring-1 ring-black/5 shadow-[0_8px_22px_-10px_rgba(0,0,0,0.35)] overflow-hidden">
          {url && <PdfCover url={url} cssWidth={260} alt={`${filename}, page 1`} />}
        </div>

        {/* PDF badge */}
        <div className="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider z-10">
          PDF
        </div>

        {/* View button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-paper-raised/90 flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all">
            <Eye className="w-5 h-5 text-ink" />
          </div>
        </div>

        {/* Bottom info */}
        <div className="absolute bottom-0 inset-x-0 p-3 bg-paper-raised">
          <p className="font-medium text-ink truncate text-sm">{filename}</p>
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-1.5 text-ink-3">
              <FileText className="w-3 h-3" />
              <span className="text-xs uppercase tracking-wide">PDF</span>
            </div>
            {displaySize && <span className="text-xs text-ink-3">{displaySize}</span>}
          </div>
        </div>
      </div>

      {url && (
        <PdfReaderDialog open={isOpen} onOpenChange={setIsOpen} url={url} title={filename} subtitle={displaySize} />
      )}
    </>
  );
}

// ── Wall YouTube Preview sub-component ─────────────────────────────────

function WallYouTubePreview({ videoId, title, thumbnail, url }: { videoId: string; title: string; thumbnail: string; url: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

  return (
    <>
      <div
        className="relative h-full flex flex-col bg-black/10 dark:bg-black/30 cursor-pointer group"
        onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
      >
        {/* Thumbnail */}
        <div className="relative flex-1 min-h-0">
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover"
            loading="lazy"
          />

          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-12 h-12 rounded-full bg-paper-raised/90 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
              <Play className="w-5 h-5 text-ink ml-0.5" fill="currentColor" />
            </div>
          </div>

          {/* YouTube badge */}
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded">
            <svg className="w-3 h-2.5" viewBox="0 0 28 20" fill="currentColor">
              <path d="M27.4 3.1s-.3-1.9-1.1-2.8C25.1-.9 23.7-.9 23-.9 19.2-1.2 14-1.2 14-1.2h0s-5.2 0-9 .3c-.7.1-2.1.1-3.3 1.2C.9 1.2.6 3.1.6 3.1S.3 5.3.3 7.6v2.1c0 2.2.3 4.5.3 4.5s.3 1.9 1.1 2.8c1.2 1.2 2.6 1.1 3.3 1.3C7.4 18.5 14 18.6 14 18.6s5.2 0 9-.3c.7-.1 2.1-.1 3.3-1.2.8-.9 1.1-2.8 1.1-2.8s.3-2.2.3-4.5V7.6c0-2.2-.3-4.5-.3-4.5zM11.1 13V5.5l6.2 3.8-6.2 3.7z" />
            </svg>
            YouTube
          </div>
        </div>

        {/* Title section */}
        <div className="p-3 bg-paper-raised min-h-[64px] flex flex-col">
          <p className="font-medium text-ink truncate text-sm mb-1">{title}</p>
          <div className="mt-auto flex items-center gap-1.5 text-ink-3">
            <Film className="w-3 h-3" />
            <span className="text-xs uppercase tracking-wide">Video</span>
          </div>
        </div>
      </div>

      {/* YouTube player modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="max-w-4xl p-0 bg-black border-none overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DialogTitle className="sr-only">Playing: {title}</DialogTitle>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={embedUrl}
              className="absolute inset-0 w-full h-full border-0"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              title={title}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
